import { Link as RouterLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import Home from '@mui/icons-material/Home';
import StoreOutlined from '@mui/icons-material/StoreOutlined';
import Store from '@mui/icons-material/Store';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import CalendarToday from '@mui/icons-material/CalendarToday';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import Person from '@mui/icons-material/Person';
import EmojiEventsOutlined from '@mui/icons-material/EmojiEventsOutlined';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import EventNoteOutlined from '@mui/icons-material/EventNoteOutlined';
import EventNote from '@mui/icons-material/EventNote';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import People from '@mui/icons-material/People';
import MenuOutlined from '@mui/icons-material/MenuOutlined';
import Menu from '@mui/icons-material/Menu';

// Icon mapping for dynamic rendering
const iconMap: Record<string, React.ComponentType> = {
  HomeOutlined,
  Home,
  StoreOutlined,
  Store,
  CalendarTodayOutlined,
  CalendarToday,
  PersonOutlined,
  Person,
  EmojiEventsOutlined,
  EmojiEvents,
  EventNoteOutlined,
  EventNote,
  PeopleOutlined,
  People,
  MenuOutlined,
  Menu,
};

interface BottomNavItemProps {
  label: string;
  path: string;
  iconOutlined: string;
  iconFilled: string;
  onPress?: () => void;
  badgeCount?: number;
}

const NavItemContainer = styled(RouterLink, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minHeight: 48, // WCAG 2.1 Level AAA - minimum touch target size
  minWidth: 48, // Ensure horizontal touch target meets minimum
  textDecoration: 'none',
  padding: theme.spacing(1, 0.5), // Increased vertical padding from 0.5 to 1
  transition: 'all 0.2s ease',
  position: 'relative',

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 4,
    left: '50%',
    transform: 'translateX(-50%)',
    width: isActive ? 4 : 0,
    height: 4,
    borderRadius: '50%',
    backgroundColor: theme.palette.venue.gaming,
    transition: 'width 0.2s ease',
  },

  '&:active': {
    transform: 'scale(0.9)',
  },
}));

const IconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.2s ease',
  '& svg': {
    fontSize: 24,
    color: isActive ? theme.palette.venue.gaming : theme.palette.text.secondary,
    transition: 'color 0.2s ease',
  },
}));

const NavLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '0.6875rem',
  fontWeight: isActive ? 600 : 500,
  color: isActive ? theme.palette.venue.gaming : theme.palette.text.secondary,
  marginTop: 2,
  transition: 'color 0.2s ease',
  lineHeight: 1,
}));

// Non-link variant for special actions (e.g., "More" tab opens drawer)
const NavItemButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minHeight: 48,
  minWidth: 48,
  cursor: 'pointer',
  padding: theme.spacing(1, 0.5),
  transition: 'all 0.2s ease',
  position: 'relative',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 4,
    left: '50%',
    transform: 'translateX(-50%)',
    width: isActive ? 4 : 0,
    height: 4,
    borderRadius: '50%',
    backgroundColor: theme.palette.venue.gaming,
    transition: 'width 0.2s ease',
  },

  '&:active': {
    transform: 'scale(0.9)',
  },
}));

export function BottomNavItem({
  label,
  path,
  iconOutlined,
  iconFilled,
  onPress,
  badgeCount,
}: BottomNavItemProps) {
  const location = useLocation();
  const isSpecialAction = path === '__drawer__';
  const isActive =
    !isSpecialAction &&
    (location.pathname === path ||
      (path === '/dashboard' && location.pathname === '/') ||
      (path !== '/dashboard' && path !== '/' && location.pathname.startsWith(path)));

  const IconComponent = iconMap[isActive ? iconFilled : iconOutlined] || iconMap[iconOutlined];

  if (isSpecialAction) {
    // Render a button regardless of whether onPress is provided to avoid falling
    // through to <RouterLink to="__drawer__"> which would navigate to a nonexistent route
    return (
      <NavItemButton
        isActive={false}
        onClick={onPress}
        role="button"
        tabIndex={0}
        aria-label={label}
        onKeyDown={(e: React.KeyboardEvent) => {
          if ((e.key === 'Enter' || e.key === ' ') && onPress) {
            e.preventDefault();
            onPress();
          }
        }}
      >
        <IconWrapper isActive={false}>{IconComponent && <IconComponent />}</IconWrapper>
        <NavLabel isActive={false}>{label}</NavLabel>
      </NavItemButton>
    );
  }

  return (
    <NavItemContainer to={path} isActive={isActive}>
      <Badge badgeContent={badgeCount} color="error" invisible={!badgeCount || badgeCount === 0}>
        <IconWrapper isActive={isActive}>{IconComponent && <IconComponent />}</IconWrapper>
      </Badge>
      <NavLabel isActive={isActive}>{label}</NavLabel>
    </NavItemContainer>
  );
}
